import mqtt, { IClientOptions, MqttClient } from 'mqtt';

type MessageCallback = (topic: string, payload: string) => void;

const decodePayload = (payload: Buffer | Uint8Array | string): string => {
  if (typeof payload === 'string') {
    return payload;
  }

  try {
    return new TextDecoder().decode(payload as Uint8Array);
  } catch (error) {
    console.error('Failed to decode MQTT payload:', error);
    return String(payload);
  }
};

export class MQTTManager {
  private client: MqttClient | null = null;
  private connectionString: string;
  private clientId: string;
  private username: string;
  private password: string;
  private onMessageCallback: MessageCallback | null = null;
  private onConnectCallback: (() => void) | null = null;
  private onDisconnectCallback: (() => void) | null = null;
  private onErrorCallback: ((error: Error) => void) | null = null;

  constructor(connectionString: string, clientId: string, username: string, password: string) {
    this.connectionString = connectionString;
    this.clientId = clientId;
    this.username = username;
    this.password = password;
  }

  connect() {
    if (this.client) {
      this.disconnect();
    }

    const options: IClientOptions = {
      clientId: this.clientId,
      clean: true,
      reconnectPeriod: 0,
      username: this.username,
      password: this.password,
    };

    this.client = mqtt.connect(this.connectionString, options);

    this.client.on('connect', () => {
      if (this.onConnectCallback) this.onConnectCallback();
    });

    this.client.on('message', (topic, payload) => {
      if (this.onMessageCallback) {
        this.onMessageCallback(topic, decodePayload(payload));
      }
    });

    this.client.on('error', (error: Error) => {
      if (this.onErrorCallback) {
        this.onErrorCallback(error);
      } else {
        console.error('MQTT error:', error);
      }
    });

    this.client.on('close', () => {
      if (this.onDisconnectCallback) this.onDisconnectCallback();
    });
  }

  disconnect() {
    if (!this.client) return;

    try {
      this.client.end(true);
    } catch (error) {
      console.error('Error disconnecting MQTT:', error);
    } finally {
      this.client = null;
    }
  }

  subscribe(topic: string, callback?: (err: Error | null) => void) {
    if (!this.client || !this.client.connected) {
      const error = new Error('Cannot subscribe: MQTT client not connected');
      if (callback) callback(error);
      return;
    }

    this.client.subscribe(topic, (err: Error | null) => {
      if (callback) callback(err);
    });
  }

  unsubscribe(topic: string) {
    if (!this.client || !this.client.connected) return;
    this.client.unsubscribe(topic);
  }

  publish(topic: string, message: string, callback?: (err?: Error) => void) {
    if (!this.client || !this.client.connected) {
      if (callback) callback(new Error('Cannot publish: MQTT client not connected'));
      return;
    }

    this.client.publish(topic, message, callback);
  }

  onMessage(callback: MessageCallback) {
    this.onMessageCallback = callback;
  }

  onConnect(callback: () => void) {
    this.onConnectCallback = callback;
  }

  onDisconnect(callback: () => void) {
    this.onDisconnectCallback = callback;
  }

  onError(callback: (error: Error) => void) {
    this.onErrorCallback = callback;
  }

  isConnected() {
    return this.client?.connected || false;
  }
}
